import { createAccessControl } from "better-auth/plugins/access";
import {
	adminAc,
	defaultStatements,
	memberAc,
} from "better-auth/plugins/organization/access";

export const organizationAc = createAccessControl({
	...defaultStatements,
	district: ["view-tournaments", "view-audit-logs"],
	roleApplication: ["review"],
	participation: ["export"],
	tournament: ["create", "view", "manage-registration", "close"],
	registration: ["approve", "register-self", "view-own-history"],
	fixture: ["generate", "assign-venue"],
	score: ["record"],
	standing: ["publish", "view-own-results"],
	profile: ["edit-self", "set-visibility", "accept-safeguarding"],
	certificate: ["download"],
});

export const organizationRoles = {
	federation_admin: organizationAc.newRole({
		...adminAc.statements,
		district: ["view-tournaments", "view-audit-logs"],
		roleApplication: ["review"],
		participation: ["export"],
		tournament: ["view"],
	}),
	tournament_operator: organizationAc.newRole({
		tournament: ["create", "view", "manage-registration", "close"],
		registration: ["approve"],
		fixture: ["generate", "assign-venue"],
		score: ["record"],
		standing: ["publish"],
	}),
	member: organizationAc.newRole({
		...memberAc.statements,
	}),
} as const;

export type OrganizationRole = keyof typeof organizationRoles;
export type EffectiveRole = OrganizationRole | "player";

export const profileVisibilityOptions = ["private", "public"] as const;

export type ProfileVisibility = (typeof profileVisibilityOptions)[number];

export const roleLabels: Record<EffectiveRole, string> = {
	federation_admin: "Federation Admin",
	tournament_operator: "Tournament Operator",
	member: "Organization Member",
	player: "Player",
};

export const roleCapabilities: Record<EffectiveRole, readonly string[]> = {
	federation_admin: [
		"Review and approve district role applications",
		"View district tournament lists",
		"Download participation exports",
		"View basic audit logs",
	],
	tournament_operator: [
		"Create tournaments",
		"Open and close registrations",
		"Approve teams and players",
		"Generate fixtures",
		"Assign venues",
		"Record scores",
		"Publish standings",
		"Close tournaments",
	],
	member: ["Access an assigned federation workspace"],
	player: [
		"Edit profile",
		"Control profile visibility",
		"Accept safeguarding policy",
		"View available tournaments",
		"Register for events",
		"View participation history and results",
		"Download participation certificates",
	],
};

export function isOrganizationRole(value: string): value is OrganizationRole {
	return value in organizationRoles;
}

export function isEffectiveRole(value: string): value is EffectiveRole {
	return value === "player" || isOrganizationRole(value);
}

export function isProfileVisibility(value: string): value is ProfileVisibility {
	return profileVisibilityOptions.includes(value as ProfileVisibility);
}
